import React from 'react';
import {
	Box,
	Modal as ChakraModal,
	HStack,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
} from '@chakra-ui/react';
import { Button } from 'newui/components/Button';
import { ModalProps } from './Modal.types';
import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiDownload, FiEdit, FiInfo, FiMail, FiPlusCircle, FiSend } from 'react-icons/fi';
import { cn } from 'utils/helpers';

const Modal: React.FC<ModalProps> = ({
	isOpen,
	description,
	headerAccessory,
	onClose,
	title,
	hideCloseButton = false,
	iconType = 'warning',
	customIcon,
	iconBgColor,
	iconColor,
	titleColor,
	children,
	primaryButton,
	secondaryButton,
	customFooter,
	closeOnOverlayClick = false,
	closeOnEsc = false,
	size = 'lg',
	isCentered = true,
	modalType = 'standard',
	titleSize = '2xl',
	trapFocus = true,
	wrapDescription = false
}) => {
	const isStandardModal = modalType === 'standard';
	const getIconConfig = () => {
		if (customIcon) {
			return { icon: customIcon, bgColor: iconBgColor || 'gray.50', color: iconColor || 'gray.500' };
		}

		switch (iconType) {
			case 'create':
				return {
					icon: <FiPlusCircle size={30} className='fill-white stroke-blue-500' />,
				};
			case 'download':
				return {
					icon: <FiDownload size={40} className='fill-blue-200 stroke-blue-500' />,
				};
			case 'edit':
				return {
					icon: <FiEdit size={30} className='fill-white stroke-blue-500' />,
				};
			case 'error':
				return {
					icon: <FiAlertCircle size={40} className='fill-red-200 stroke-red-700' />,
				};
			case 'info':
				return {
					icon: <FiInfo size={40} className='fill-blue-200 stroke-blue-500' />,
				};
			case 'mail':
				return {
					icon: <FiMail size={40} className='fill-blue-200 stroke-blue-500' />,
				};
			case 'success':
				return {
					icon: <FiCheckCircle size={40} className='fill-blue-200 stroke-blue-600' />
				};
			case 'warning':
				return {
					icon: <FiAlertTriangle size={40} className='fill-yellow-500 stroke-black' />,
				};
			default:
				return {
					icon: null,
					bgColor: 'black.50',
					color: 'black.50',
				};
		}
	};

	const getTitleColor = () => {
		if (titleColor) {
			return titleColor;
		}

		if (customIcon) {
			return 'gray.800';
		}

		switch (iconType) {
			case 'error':
				return 'red.700';
			case 'warning':
				return 'yellow.600';
			case 'info':
			case 'mail':
				return 'blue.500';
			case 'success':
				return 'blue.600';
			default:
				return 'gray.800';
		}
	};

	const iconConfig = getIconConfig();
	const computedTitleColor = getTitleColor();

	const maxWidthMap: Record<string, string | undefined> = {
		sm: '400px',
		md: '500px',
		lg: '600px',
		xl: '800px',
		xxl: '1200px',
		'6xl': undefined,
		full: undefined,
	};

	const isFullSize = size === 'full' || size === '6xl';

	const titleIsText = typeof title === 'string';

	const renderHeader = () => {
		return modalType === 'alert' ? <ModalHeader
			pt={8}
			pb={4}
			px={6}
			borderBottom="1px solid"
			borderColor="gray.100"
		>
			<VStack spacing={3} align="center" className='flex items-center'>
				<Box borderRadius="full" bg={iconConfig.bgColor}>
					{React.isValidElement(iconConfig.icon) ? (
						React.cloneElement(iconConfig.icon as React.ReactElement, {
							stroke: iconConfig.color,
						})
					) : (
						iconConfig.icon
					)}
				</Box>
				{titleIsText ? <Text fontSize={titleSize} fontWeight="bold" color={computedTitleColor} textAlign="center">
					{title}
				</Text> : title}
				{description && <Text>
					{description}
				</Text>}
			</VStack>
		</ModalHeader> : <ModalHeader
			borderBottom="1px solid"
			borderColor="gray.100"
		>
			<VStack align="stretch" spacing={headerAccessory ? 3 : 0} w="full">
				<HStack align={"left"} spacing={5} className='flex items-center'>
					<Box borderRadius="full" bg={iconConfig.bgColor}>
						{React.isValidElement(iconConfig.icon) ? (
							React.cloneElement(iconConfig.icon as React.ReactElement, {
								stroke: iconConfig.color,
							})
						) : (
							iconConfig.icon
						)}
					</Box>
					<div className={cn("flex text-left gap-0 items-center"
						, wrapDescription ? 'flex-row' : 'flex-col'
					)}>

						<Text fontSize={titleSize} fontWeight="bold" color={computedTitleColor}>
							{title}
						</Text>
						{description && (typeof description === "string" ? <Text fontSize="md" fontWeight="normal" marginTop={wrapDescription ? 1 : 0} marginLeft={wrapDescription ? 4 : 0}>
							{description}
						</Text>
							: description)}
					</div>
				</HStack>
				{headerAccessory && <Box w="full">{headerAccessory}</Box>}
			</VStack>
		</ModalHeader>

	}

	const renderFooter = () => {
		if (customFooter) {
			return customFooter;
		}

		if (!primaryButton && !secondaryButton) {
			return null;
		}

		return (
			<div className={isStandardModal ? "flex w-full justify-end gap-2" : "flex w-full justify-center gap-3"}>
				{primaryButton && (
					<Button
						onClick={primaryButton.onClick}
						btntype={primaryButton.btntype || 'primary'}
						size={primaryButton.size ?? "sm"}
						width={primaryButton.width ?? (isStandardModal ? "100px" : undefined)}
						disabled={primaryButton.disabled}
						isLoading={primaryButton.isLoading}
					>
						<span className={primaryButton.icon && 'pr-2'}>{primaryButton.icon}</span>
						{primaryButton.text}
					</Button>
				)}
				{secondaryButton && (
					<Button
						onClick={secondaryButton.onClick}
						btntype={secondaryButton.btntype || 'secondary'}
						size={secondaryButton.size ?? "sm"}
						width={secondaryButton.width ?? (isStandardModal ? "100px" : undefined)}
						disabled={secondaryButton.disabled}
						isLoading={secondaryButton.isLoading}
					>
						<span className={secondaryButton.icon && 'pr-2'}>{secondaryButton.icon}</span>
						{secondaryButton.text}
					</Button>
				)}
			</div>
		);
	};

	return (
		<ChakraModal
			isOpen={isOpen}
			onClose={onClose}
			isCentered={isCentered}
			closeOnOverlayClick={closeOnOverlayClick}
			closeOnEsc={closeOnEsc}
			trapFocus={trapFocus}
			motionPreset="scale"
			size={size}
			scrollBehavior={isFullSize ? 'inside' : undefined}
		>
			<ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
			<ModalContent
				borderRadius="xl"
				bg="white"
				boxShadow="2xl"
				maxW={maxWidthMap[size]}
				mx="auto"
				{...(isFullSize && { maxH: '95vh', display: 'flex', flexDirection: 'column' })}
			>
				{!hideCloseButton && <ModalCloseButton className="z-[999]" size={"md"} />}
				{renderHeader()}

				<ModalBody
					px={6}
					pt={6}
					pb={2}
					{...(isFullSize && { flex: 1, overflowY: 'auto' })}
				>
					<VStack spacing={6} align="stretch">
						{children}
					</VStack>
				</ModalBody>

				{(customFooter || primaryButton || secondaryButton) && (
					<ModalFooter px={6} pb={4} pt={2} flexDirection="column" gap={3}>
						{renderFooter()}
					</ModalFooter>
				)}
			</ModalContent>
		</ChakraModal>
	);
};

export default Modal;
